import React, { useEffect, useState } from 'react';
import { consoleTokens } from '@components/ui/console';
import {
  Box,
  Dialog,
  InputBase,
  Stack,
  Typography,
} from '@mui/material';

/** One reported config line shown in the preview ("Grouping → by clinician"). */
export interface SaveViewSummaryRow {
  label: string;
  value: string;
  /** Render the value in mono (filters, codes); plain otherwise. */
  mono?: boolean;
}

interface SaveViewDialogProps {
  open: boolean;
  reportTitle: string;
  windowDays: number;
  periodLabel: string;
  /** The non-window config (basis, grouping, columns, filters) being saved. */
  summary: SaveViewSummaryRow[];
  onClose: () => void;
  onSave: (name: string, windowKind: 'relative_days' | 'absolute') => void;
  isSaving: boolean;
}

const WINDOW_OPTIONS: {
  value: 'relative_days' | 'absolute';
  title: string;
  hint: (windowDays: number, periodLabel: string) => string;
}[] = [
  {
    value: 'relative_days',
    title: 'Rolling window',
    hint: windowDays => `always the last ${windowDays} days at run time`,
  },
  {
    value: 'absolute',
    title: 'Fixed dates',
    hint: (_windowDays, periodLabel) => `always ${periodLabel}`,
  },
];

const labelSx = {
  color: consoleTokens.fainter,
  fontSize: '0.625rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
};

/**
 * Names and saves the current report configuration, in the console design
 * language. A preview lists the exact rules being captured so the saved view
 * is never a mystery; the window choice (rolling vs fixed dates) is the one
 * decision the preview can't infer, so it leads.
 */
export const SaveViewDialog: React.FC<SaveViewDialogProps> = ({
  open,
  reportTitle,
  windowDays,
  periodLabel,
  summary,
  onClose,
  onSave,
  isSaving,
}) => {
  const [name, setName] = useState('');
  const [windowKind, setWindowKind] = useState<'relative_days' | 'absolute'>(
    'relative_days',
  );

  // Fresh dialog each open — a stale name/window from a prior save is a trap.
  useEffect(() => {
    if (open) {
      setName('');
      setWindowKind('relative_days');
    }
  }, [open]);

  const canSave = Boolean(name.trim()) && !isSaving;
  const handleSave = () => {
    if (canSave) onSave(name.trim(), windowKind);
  };

  const periodValue =
    windowKind === 'relative_days' ? `Last ${windowDays} days` : periodLabel;
  const periodHint =
    windowKind === 'relative_days'
      ? 're-resolves every run'
      : 'pinned to these dates';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="save-view-title"
      slotProps={{ paper: { sx: { borderRadius: '10px' } } }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography
          id="save-view-title"
          component="h2"
          sx={{ color: consoleTokens.ink, fontSize: '0.9375rem', fontWeight: 600 }}
        >
          Save report view
        </Typography>
        <Typography sx={{ color: consoleTokens.dim, fontSize: '0.8125rem', mt: 0.5 }}>
          Capture this {reportTitle} setup to rerun in one click.
        </Typography>

        {/* Name */}
        <Box
          sx={{
            border: `1px solid ${consoleTokens.line}`,
            borderRadius: '8px',
            mt: 2,
            px: 1.5,
            py: 1,
            '&:focus-within': { borderColor: consoleTokens.ink },
          }}
        >
          <Typography sx={{ ...labelSx, mb: 0.25 }}>Name</Typography>
          <InputBase
            autoFocus
            fullWidth
            placeholder="e.g. Biweekly payroll — by clinician"
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleSave();
            }}
            inputProps={{ maxLength: 120, 'aria-label': 'View name' }}
            sx={{ color: consoleTokens.ink, fontSize: '0.875rem' }}
          />
        </Box>

        {/* Window choice — segmented, console-style */}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          {WINDOW_OPTIONS.map(option => {
            const selected = windowKind === option.value;
            return (
              <Box
                key={option.value}
                component="button"
                type="button"
                onClick={() => setWindowKind(option.value)}
                sx={{
                  bgcolor: selected ? consoleTokens.tokenHoverBg : consoleTokens.tokenBg,
                  border: `1px solid ${selected ? consoleTokens.ink : consoleTokens.line}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  flex: 1,
                  px: 1.5,
                  py: 1,
                  textAlign: 'left',
                }}
              >
                <Typography
                  sx={{
                    color: consoleTokens.ink,
                    fontSize: '0.8125rem',
                    fontWeight: selected ? 600 : 500,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography sx={{ color: consoleTokens.dim, fontSize: '0.6875rem', mt: 0.25 }}>
                  {option.hint(windowDays, periodLabel)}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {/* Preview — the exact rules being saved */}
        <Box
          sx={{
            bgcolor: consoleTokens.bandBg,
            border: `1px solid ${consoleTokens.line}`,
            borderRadius: '8px',
            mt: 2,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography sx={{ ...labelSx, mb: 0.75 }}>This view runs</Typography>
          <Stack spacing={0.5}>
            <SummaryLine
              label="Period"
              value={periodValue}
              hint={periodHint}
            />
            {summary.map(row => (
              <SummaryLine
                key={row.label}
                label={row.label}
                value={row.value}
                mono={row.mono}
              />
            ))}
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end', mt: 2.5 }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              bgcolor: 'transparent',
              border: 'none',
              color: consoleTokens.dim,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              px: 1.5,
              py: 0.75,
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            sx={{
              bgcolor: canSave ? consoleTokens.ink : consoleTokens.tokenBg,
              border: 'none',
              borderRadius: '8px',
              color: canSave ? '#FFFFFF' : consoleTokens.fainter,
              cursor: canSave ? 'pointer' : 'default',
              fontSize: '0.8125rem',
              fontWeight: 600,
              px: 2,
              py: 0.75,
            }}
          >
            {isSaving ? 'Saving…' : 'Save view'}
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
};

const SummaryLine: React.FC<{
  label: string;
  value: string;
  hint?: string;
  mono?: boolean;
}> = ({ label, value, hint, mono }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
    <Typography
      sx={{ color: consoleTokens.dim, flexShrink: 0, fontSize: '0.75rem', width: 72 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        color: consoleTokens.ink,
        fontFamily: mono ? consoleTokens.mono : undefined,
        fontSize: '0.75rem',
        fontWeight: 500,
      }}
    >
      {value}
      {hint && (
        <Box component="span" sx={{ color: consoleTokens.fainter, fontWeight: 400 }}>
          {' '}
          · {hint}
        </Box>
      )}
    </Typography>
  </Stack>
);
